/**
 * BuildPart builders for coarse stand-ins.
 *
 * Deliberately reuses the build system's `BuildPart` / `sdfFromConfig` vocabulary rather than
 * inventing a stamp-only one — the Torch stamp already goes through it at LOD 0. Sizes are in FINE
 * voxels at every level; the rasterizer divides by the LOD stride, so one stand-in stays valid
 * across every level it serves.
 *
 * Size semantics come from sdfFromConfig: sphere reads size.x as its radius, cylinder reads size.x
 * as radius and size.y as HALF-height.
 *
 * TWO RULES GOVERN EVERY BUILDER HERE, both consequences of `bestWeight > 0` in the rasterizer —
 * a coarse voxel is emitted only where the SDF is STRICTLY inside:
 *
 *  1. PARTS MUST OVERLAP, NEVER MERELY TOUCH. (For trees this now falls out of the model itself: a
 *     trunk runs PAST the crown base up to `trunkTop`, so the two always share rows. Buildings do the
 *     same: the wall runs one stride PAST the eave, up inside the roof. Sinking the ROOF instead —
 *     starting the cone a stride below the eave — is what a building tried first, and it puts the
 *     cone's stated base radius below the wall top so the radius has already tapered by the time it
 *     gets there: measured on the hut at lod 1, the drawn roof radius at the eave was 8.9 fine voxels
 *     against LOD 0's 11.7, a cap sinking into its own drum. Extrapolating the base to compensate
 *     fixes the eave and fattens everything below it — a 10 m roof on a 7 m hut at lod 3.) Where two parts share a face the SDF is exactly zero
 *     on both, so NEITHER emits and the row comes out empty. That was the visible gap between a pine
 *     trunk and its canopy (trunk's last row 7, canopy's first row 9, row 8 missing) and the gaps
 *     between stack steps. Oak escaped it only by accident — its canopy is a sphere whose underside
 *     dips past the trunk top.
 *  2. NO SUB-VOXEL PARTS. A cylinder thinner than the lattice still contains the lattice point on its
 *     own axis, so it emits a ONE-VOXEL-WIDE COLUMN — a spike. A pine's tip step is radius
 *     `baseR * 0.25` = 1.5 fine voxels = 0.375 of a lod-2 voxel, which is exactly the spike above the
 *     leaves. Steps below the threshold are DROPPED and the step below them extended to the crown,
 *     rather than clamped: clamping several tip steps to the same minimum just makes a fatter spike.
 */
import { BuildMode, BuildShape } from '../../voxel/buildTypes.js';
import { PIN_COARSE_RADIUS, pinSpanPart } from '../../voxel/buildParts.js';
/**
 * How far (fine voxels) a ground-contacting stand-in extends BELOW the stamp origin.
 *
 * A coarse voxel only emits where the SDF is strictly inside, so a part whose base sits exactly at
 * y=0 first emits at the coarse row ABOVE the surface — the model floats by up to a coarse voxel.
 * On top of that the coarse surface itself sits up to a coarse voxel away from the fine one (the
 * vertical-anchor problem in docs/lod-far-view-system.md), so "exactly seated" is not achievable and
 * not what we want: sink it. The buried voxels are inside solid terrain and never drawn.
 */
export const GROUND_SINK = 4;
/**
 * A one-voxel-wide column from the ground to `topY`, fully solid.
 *
 * Serves the trunk at lod 3 and the WHOLE TREE at lod 4+. A trunk wide enough to render as a body at
 * lod 3 would need a 2 m radius — as wide as the canopy it holds up, i.e. a pillar — so thin is not a
 * compromise, it is the shape.
 *
 * Height comes from the TREE, not the stride. The stride-sized sphere this replaced emitted exactly
 * one row at every level: 4 m at lod 4 whether the tree was a 6.4 m oak or a 9.5 m pine, so a pine
 * rendered at under half height. Half of it was also buried below ground.
 */
export function pinPart(topY, material, stride) {
    return pinSpanPart(-groundSink(stride), topY, material, stride);
}
/**
 * The sink for a given stride, in fine voxels: at least a whole COARSE voxel.
 *
 * A fixed 4 fine voxels is a full voxel at lod 1 but only an eighth of one at lod 3, so the bottom
 * row of a sunk part ended up barely inside it — measured at weight 0.23 (lod 3) and 0.13 (lod 4)
 * against a solid 0.50, i.e. the base of every distant tree faded out instead of meeting the ground.
 */
export function groundSink(stride) {
    return Math.max(GROUND_SINK, stride);
}
/**
 * The half-extent a part of half-extent `half` will actually DRAW to — the last lattice point
 * strictly inside it, since that is the only place a coarse voxel is emitted.
 *
 * WHY THERE IS NO "ROUND IT UP" HELPER BESIDE THIS ONE. Rounding every building part up to the
 * nearest lattice point was the obvious reading of "coarse buildings come out narrower than LOD 0",
 * and it is wrong: a drawn voxel covers its own CELL, so a part that draws to `k*stride` spans
 * `2*k*stride + stride`, not `2*k*stride`. Measured against LOD 0's span, the floor this function
 * describes is already the closest lattice choice at every level (hut radius 3.5 m: 6 m drawn against
 * 7 m real at lod 3, where rounding up gives 10 m), so the undersizing to fix is not the WIDTH of a
 * part — it is the roof losing its relationship to the wall (`roofCapsWall`) and the cone's base
 * radius landing below the eave (`coneBaseForEave`).
 */
function drawnHalf(half, stride) {
    return Math.max(0, Math.ceil(half / stride) - 1) * stride;
}
/**
 * A roof's half-extent, raised if need be so the roof still visibly caps the wall it sits on. For the
 * GABLE, whose prism draws its base row at full width; a cone needs `roofConeBase` instead.
 *
 * LOD 0 overhangs by exactly one FINE voxel (radius + 1 against radius), which is sub-voxel at every
 * coarse stride — so wall and roof round to the same lattice point and the roof stops reading as a
 * roof. This keeps the RELATIONSHIP rather than the number, the same move as the deliberately fat
 * lod-1 tree trunk: one clear lattice point beyond whatever the wall draws to.
 *
 * Only while the roof still has rows to be a roof in (stride <= 4, i.e. lod 1-2). At lod 3 a forced
 * overhang would be a whole 2 m voxel on a 5.5 m hut — 82% too wide — so from there the roof shares
 * the wall's extent and is read by its material and its taper.
 */
export function roofCapsWall(roofRadius, wallRadius, stride) {
    if (stride > 4)
        return roofRadius;
    return Math.max(roofRadius, drawnHalf(wallRadius, stride) + 1.25 * stride);
}
/**
 * Base radius for a conical roof spanning [y0, y1] over a wall of radius `wallRadius`, such that the
 * roof's FIRST DRAWN ROW is as wide as LOD 0's roof is at the eave — and, while the level can still
 * show it, one lattice point wider than the wall.
 *
 * THE FIRST DRAWN ROW IS NOT THE BASE. A cone emits only where its SDF is strictly inside, so its base
 * plane never emits and its first row sits up to a stride higher, by which point a 3 m roof over a
 * 13-fine-voxel rise has already lost a fifth of its radius. Stating the base radius and hoping was
 * the first attempt: measured on the hut at lod 1, the drawn roof came out at 8.9 fine voxels against
 * LOD 0's 11.7 at the eave — a cap sunk into its own drum, which is what "the roof is too small" looks
 * like. So the target is applied one stride up and the base is back-solved from it, which fattens only
 * the band between the eave and the first drawn row — a band the wall occupies anyway.
 *
 * THE WALL FLOOR (`stride <= 4`) is the other half. LOD 0 overhangs by one FINE voxel — sub-voxel at
 * every coarse stride — so without a floor the wall and the roof round to the same lattice point and
 * the roof stops reading as a roof. It keeps the RELATIONSHIP rather than the number, the same move as
 * the deliberately fat lod-1 tree trunk. Above lod 2 a forced overhang would be a whole 2 m voxel on a
 * 5.5 m hut, so from there the roof shares the wall's extent and is read by material and taper.
 */
export function roofConeBase(eaveRadius, wallRadius, tipRadius, y0, y1, stride) {
    const target = stride <= 4
        ? Math.max(eaveRadius, drawnHalf(wallRadius, stride) + 1.25 * stride)
        : eaveRadius;
    const f = stride / (y1 - y0); // fraction of the cone climbed by the first drawn row
    if (!(f > 0) || f >= 1)
        return target;
    return (target - tipRadius * f) / (1 - f);
}
/**
 * Tip radius for a conical roof spanning [y0, y1] on a `stride` lattice: the real tip while there are
 * rows to resolve a taper, the BASE radius (i.e. a drum) below three of them.
 *
 * The pine's crown reached the same rule from the other direction. Measured on the hut at lod 3, a
 * 3.25 m roof is 1.6 coarse rows and the cone's top row packed to 0.03 against a solid 0.50 — a faint
 * speck floating over the roof, because the row sits on the cone's own apex where the SDF is ~0. A
 * drum of the same height and colour has the mass the silhouette needs and no apex to sit on.
 */
export function roofTipRadius(baseRadius, y0, y1, stride) {
    if ((y1 - y0) / stride < 3)
        return baseRadius;
    // Bounded by the LATTICE as well as by the roof: under one coarse voxel of radius a cone renders as
    // its bare axis, i.e. a needle rather than a point.
    return Math.max(1, 0.8 * stride);
}
/**
 * The half-extent to STATE for a box so that what it DRAWS best matches the real thing — ties broken
 * upward, because a building that swells as you approach is the artifact and one that is a voxel too
 * wide at 300 m is not.
 *
 * The comparison is on SPANS, not on half-extents: a drawn voxel covers its own cell, so a box drawing
 * to `k*stride` spans `2*k*stride + stride`. That is why there is no blanket "round every part up" —
 * measured against LOD 0's span, the plain floor is already the closest choice at most levels. Where it
 * is NOT is a tie: a 12 m house at lod 3 is equally close drawn 10 m or 14 m, and the floor silently
 * picks 10.
 */
export function blockHalf(half, stride) {
    const k = Math.round(half / stride - 0.5); // Math.round breaks the .5 tie upward
    return coarseSolidHalf((k + 0.25) * stride, stride);
}
/**
 * A half-extent held to at least one coarse voxel — rule 2 above, for a part that is meant to read as
 * a BODY rather than a mark. A 6 m house at lod 4 is 0.75 of a voxel wide: the lattice point on its
 * axis is inside, so it emits a single column at weight 0.375 against a solid 0.5, i.e. a faded
 * spike. Widened it emits the same one column, fully solid — the honest picture of a house that is
 * now smaller than a voxel, and the same trick the pine's crown uses at its coarsest levels.
 */
function coarseSolidHalf(half, stride) {
    return Math.max(half, PIN_COARSE_RADIUS * stride);
}
/**
 * How far below the stamp origin a BUILDING's stand-in reaches, in fine voxels.
 *
 * A tree needs only `groundSink` — one coarse voxel, enough that its base row reads solid. A building
 * has a 5-12 m FOOTPRINT, and a coarse stamp is seated on the single lattice height of its own
 * column (StampPlacer), so wherever the ground falls away across that footprint the box's downhill
 * corner hangs in the air. Half the footprint's half-width covers the relief of a typical slope and
 * is buried on flat ground, where it is the foundation the LOD-0 model has anyway.
 *
 * generateLodColumn subtracts the plinth from the column's solid bound (it reads the model's own
 * bounds — see stampClipMarginFine), or the plinth lands in a chunk the clip has promised is rock.
 */
export function buildingSink(halfFootprint, stride) {
    return Math.max(groundSink(stride), Math.round(halfFootprint * 0.5));
}
/**
 * A lamp: one emissive blob, for a building's exterior torches at coarse LODs.
 *
 * THE RADIUS IS FORCED, not chosen. Block light is seeded from `VOXEL_EMISSION`, which only fires on
 * a voxel that packs SOLID — so a faithfully sub-voxel torch flame emits nothing at all, and the
 * smallest thing that can be a light is one full coarse voxel.
 *
 * Its position is forced too: `lava` is a LIQUID-type material, so a lamp voxel inside the wall would
 * take that voxel out of the solid mesh layer and leave a hole in it (the same defect transparent
 * leaves cause). The lamp therefore stands just clear of the wall in air, as the LOD-0 torch flame
 * does — which is also the only place its light can propagate from.
 */
export function lampPart(y, z, material, stride, snapToLattice = false) {
    const r = PIN_COARSE_RADIUS * stride;
    // ONE VOXEL, WHERE ONE VOXEL IS ALL THERE SHOULD BE. A sphere of this radius centred OFF the lattice
    // catches two rows either side of the offset — at lod 5 a lamp 8 fine voxels up from the row landed on
    // both rows and both z columns, four voxels of light beside a building drawn with one. Snapping the
    // centre onto the lattice makes the neighbours exactly `stride` away, i.e. outside, so the lamp is the
    // single voxel it is meant to be. Below lod 4 it is NOT snapped: there it is a real blob beside a real
    // wall, and its position matters more than its voxel count.
    const cy = snapToLattice ? Math.round(y / stride) * stride : y;
    const cz = snapToLattice ? Math.round(z / stride) * stride : z;
    return {
        config: {
            shape: BuildShape.SPHERE,
            mode: BuildMode.ADD,
            size: { x: r, y: r, z: r },
            material,
        },
        offset: { x: 0, y: cy, z: cz },
    };
}
/**
 * Where a lamp's CENTRE goes, in fine voxels along −Z, for a building whose front face is `faceZ` out.
 *
 * Below lod 4 it stands clear of the wall — `1.1 * stride` is past PIN_COARSE_RADIUS, so the blob stops
 * short of the wall face and never takes a voxel out of the solid layer. From lod 4 the building is a
 * one-voxel pin with no wall to stand off, and a standoff measured from its full-size footprint would
 * park the lamp two or three voxels away from a building that is one voxel wide — so it sits exactly
 * one voxel to the side, which is as close as it can be without contesting the pin's own cell.
 */
export function lampOffsetZ(faceZ, stride, lod) {
    return lod >= 4 ? -stride : -(faceZ + 1.1 * stride);
}
//# sourceMappingURL=coarseModel.js.map